/**
 * 统一 fetch 封装(应用侧唯一出口)。
 *
 * 语义(与 packages/api-client 的 orval mutator 保持完全一致,两边改一处必须同步另一处):
 *  1. 同源请求,credentials: 'same-origin' —— Django session cookie 自动带上;
 *     dev 期由 Vite 的 /api 代理转发到 127.0.0.1:8000,浏览器视角始终同源。
 *  2. 非安全方法(POST/PUT/PATCH/DELETE)自动从 csrftoken cookie 读值写入 X-CSRFToken 头。
 *  3. 非 2xx 一律抛 ApiError(带 status / code / 字段级错误),调用方不需要判 res.ok。
 *  4. 401 触发未登录处理:跳 /login?next=<当前路径>(处理器由 main.tsx 注入 router)。
 *
 * ★ **错误身份只有一个**:`ApiError` / `ApiErrorBody` / `isApiError` 一律 re-export
 *   `@gta/api-client`(真源在 packages/api-client/src/mutator.ts,包不能反向依赖应用)。
 *   本文件的 `apiFetch` 抛的和生成 client 抛的是同一个类,`instanceof` 恒等 ——
 *   业务域不再需要任何「换身份」适配层。既有 `@/lib/api` 的 import 路径全部照旧可用。
 */

import { ApiError, type ApiErrorBody } from '@gta/api-client'

export { ApiError, isApiError } from '@gta/api-client'
export type { ApiErrorBody } from '@gta/api-client'

/** Django 默认 CSRF cookie 名。 */
export const CSRF_COOKIE_NAME = 'csrftoken'
/** Django 默认 CSRF 请求头名。 */
export const CSRF_HEADER_NAME = 'X-CSRFToken'
/** 后端 API 前缀(经 Vite 代理 → Django)。 */
export const API_BASE_URL = '/api/v1'

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE'])

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS'

/* -------------------------------------------------------------------------- */
/* cookie                                                                      */
/* -------------------------------------------------------------------------- */

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  for (const raw of document.cookie.split(';')) {
    const chunk = raw.trim()
    if (chunk.startsWith(prefix)) {
      return decodeURIComponent(chunk.slice(prefix.length))
    }
  }
  return null
}

export function readCsrfToken(): string | null {
  return readCookie(CSRF_COOKIE_NAME)
}

/**
 * 确保 csrftoken cookie 已种下(登录前必须先调一次)。
 * 后端:GET /api/v1/auth/csrf 挂 @ensure_csrf_cookie。
 */
export async function ensureCsrfCookie(): Promise<void> {
  if (readCsrfToken()) return
  await apiFetch<unknown>('/auth/csrf', { skipUnauthorizedHandler: true })
}

/* -------------------------------------------------------------------------- */
/* 401 处理器注入                                                              */
/* -------------------------------------------------------------------------- */

export type UnauthorizedHandler = (next: string) => void

/** 兜底:router 尚未接管时用整页跳转,保证任何入口都不会卡在空白页。 */
const defaultUnauthorizedHandler: UnauthorizedHandler = (next) => {
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/login') return
  window.location.assign(`/login?next=${encodeURIComponent(next)}`)
}

let unauthorizedHandler: UnauthorizedHandler = defaultUnauthorizedHandler

/** main.tsx 在 router 就绪后注入,把整页跳转换成 router.navigate。 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler
}

/** 当前地址(path + search + hash),作为 ?next= 的值。 */
export function currentInternalHref(): string {
  if (typeof window === 'undefined') return '/'
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

/**
 * 收敛 ?next= 的取值面,挡开放重定向:只放行站内绝对路径。
 */
export function sanitizeNextPath(next: string | undefined | null, fallback = '/dashboard'): string {
  if (!next) return fallback
  if (!next.startsWith('/')) return fallback
  if (next.startsWith('//')) return fallback
  if (next.startsWith('/login')) return fallback
  return next
}

/* -------------------------------------------------------------------------- */
/* 请求                                                                        */
/* -------------------------------------------------------------------------- */

export type QueryParamValue = string | number | boolean | null | undefined
export type QueryParams = Record<string, QueryParamValue | QueryParamValue[]>

export interface ApiRequestOptions {
  method?: HttpMethod | undefined
  /** 自动 JSON 序列化;需要发 FormData 时直接传 FormData(不加 Content-Type)。 */
  body?: unknown
  params?: QueryParams | undefined
  headers?: Record<string, string> | undefined
  signal?: AbortSignal | undefined
  /** 登录页探测当前身份等场景:401 属于预期结果,不要触发跳转。 */
  skipUnauthorizedHandler?: boolean | undefined
}

export function buildQueryString(params: QueryParams | undefined): string {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue
        search.append(key, String(item))
      }
    } else {
      search.append(key, String(value))
    }
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

function resolveUrl(path: string, params: QueryParams | undefined): string {
  const base = /^https?:\/\//.test(path) || path.startsWith('/api/') ? path : `${API_BASE_URL}${path}`
  return `${base}${buildQueryString(params)}`
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    try {
      return (await response.json()) as unknown
    } catch {
      return undefined
    }
  }
  const text = await response.text()
  return text.length > 0 ? text : undefined
}

function messageFromBody(body: unknown, status: number): string {
  if (typeof body === 'string' && body.trim().length > 0) return body.trim().slice(0, 300)
  if (body && typeof body === 'object') {
    const record = body as ApiErrorBody
    if (typeof record.detail === 'string') return record.detail
    if (Array.isArray(record.non_field_errors) && typeof record.non_field_errors[0] === 'string') {
      return record.non_field_errors[0]
    }
    for (const value of Object.values(record)) {
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
      if (typeof value === 'string') return value
    }
  }
  return defaultMessageForStatus(status)
}

function defaultMessageForStatus(status: number): string {
  switch (status) {
    case 0:
      return '无法连接后端服务,请确认 Django 已在 127.0.0.1:8000 启动'
    case 400:
      return '请求参数有误'
    case 401:
      return '登录已失效,请重新登录'
    case 403:
      return '没有权限执行该操作'
    case 404:
      return '请求的资源不存在'
    case 409:
      return '操作与当前数据状态冲突'
    case 500:
      return '服务器内部错误'
    default:
      return `请求失败(HTTP ${status})`
  }
}

/**
 * 发起一次 API 请求。成功返回已解析的 JSON(204 返回 undefined),失败抛 ApiError。
 */
export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const url = resolveUrl(path, options.params)

  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')
  headers.set('X-Requested-With', 'XMLHttpRequest')

  let payload: BodyInit | undefined
  if (options.body instanceof FormData) {
    payload = options.body
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    payload = JSON.stringify(options.body)
  }

  if (!SAFE_METHODS.has(method)) {
    const token = readCsrfToken()
    if (token) headers.set(CSRF_HEADER_NAME, token)
  }

  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers,
      credentials: 'same-origin',
      ...(payload === undefined ? {} : { body: payload }),
      ...(options.signal ? { signal: options.signal } : {}),
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError({
      message: defaultMessageForStatus(0),
      status: 0,
      url,
      code: 'network_error',
    })
  }

  const body = await parseBody(response)

  if (!response.ok) {
    const errorBody = body && typeof body === 'object' ? (body as ApiErrorBody) : undefined
    const error = new ApiError({
      message: messageFromBody(body, response.status),
      status: response.status,
      url,
      code: typeof errorBody?.code === 'string' ? errorBody.code : undefined,
      body: errorBody,
    })
    if (error.status === 401 && !options.skipUnauthorizedHandler) {
      unauthorizedHandler(currentInternalHref())
    }
    throw error
  }

  return body as T
}

export const api = {
  get: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
} as const
